import { MainApi } from 'api/MainApi'
import { RobotApi } from 'api/RobotApi'
import { EventStatistics, OdysseyEventStatistics } from 'api/schema/models/EventStatistics'
import Box from 'components/Box/Box'
import { Button } from 'components/Button/Button'
import Title from 'components/Title/Title'
import { eachDayOfInterval, endOfDay, format, parseISO, startOfDay, subDays, subWeeks } from 'date-fns'
import { exportScheduleToCSV } from 'pages/TaskScheduler/TaskSchedulerExport'
import { ProcessedEvent } from 'pages/TaskScheduler/TaskSchedulerUtils'
import { addEventsStatistics } from 'pages/TaskScheduler/TaskSchedulerUtils'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type { VendorCode } from 'utils/vendorDisplayName'
import { VENDOR } from 'utils/vendorDisplayName'

import style from './Dashboard.module.css'

interface ChartData {
  date: string
  area: number
  time: number
  overallTime: number
  eventsCount: number
  fullDate: string
}

type NormalizedItem = {
  vendor_origin_record_type: 'event_statistics' | 'odyssey_event_statistics' | 'gausium_task_report'
  event_id: string
  robot_sn: string
  name: string
  start_time: string
  created_at: string
  end_time?: string | null
  cleaned_area_actual: number
  cleaning_performance: number
  duration_actual: number
  battery_begin: number
  battery_end: number
  vendor?: VendorCode
  /** Upstream record id: MissionJob.id, Odyssey.id, or GS task report UUID */
  vendor_origin_record_id?: number | string
  robot_id?: number | null
  scenario_or_map_id?: number | string
  mission_or_area_id?: number | string
  duration_planned?: number | null
  duration_overall?: number | null
  missions_info?: Array<{ id?: number | string; name?: string }>
  missions_progress?: Array<Record<string, unknown>>
}

const FACILITY_NONE_ID = '__none__'
const FACILITY_NONE_LABEL = 'No facility'

function normalizedToEventLike(n: NormalizedItem): EventStatistics & { vendor?: VendorCode; map?: string } {
  const missionId = typeof n.mission_or_area_id === 'number' ? n.mission_or_area_id : 0
  const mapVal = typeof n.scenario_or_map_id === 'string' ? n.scenario_or_map_id : undefined
  const origin = n.vendor_origin_record_id
  const displayAt = n.vendor === VENDOR.GAUSIUM && n.end_time ? n.end_time : n.start_time || n.created_at
  return {
    id: 0,
    event_id: n.event_id,
    task_id: n.vendor_origin_record_type === 'event_statistics' && typeof origin === 'number' ? origin : (0 as any),
    robot_sn: n.robot_sn as any,
    status: 0,
    mission_id: missionId,
    mission_status: 0,
    mission_progress: {
      name: n.name,
      cleaned_area_actual: n.cleaned_area_actual,
      duration_actual: n.duration_actual,
      cleaning_performance: n.cleaning_performance,
      duration_overall: n.duration_overall ?? n.duration_actual,
      duration_planned: n.duration_planned ?? 0,
      travel_distance_planned: 0,
      travel_distance_actual: 0,
      water_level_begin: 0,
      water_level_end: 0,
      dirty_water_level_begin: 0,
      dirty_water_level_end: 0,
      garbage_filled_begin: 0,
      garbage_filled_end: 0,
      battery_begin: n.battery_begin,
      battery_end: n.battery_end,
    },
    created_at: displayAt,
    updated_at: displayAt,
    ...(n.vendor === VENDOR.GAUSIUM && { vendor: VENDOR.GAUSIUM as VendorCode, map: mapVal }),
    ...(typeof n.scenario_or_map_id === 'number' && { scenario_id: n.scenario_or_map_id }),
  } as EventStatistics & { vendor?: VendorCode; map?: string }
}

function normalizedToOdysseyLike(n: NormalizedItem): OdysseyEventStatistics {
  const progress = n.missions_progress?.[0] as Record<string, unknown> | undefined
  const overall = {
    name: n.name,
    cleaned_area_actual: n.cleaned_area_actual,
    duration_actual: n.duration_actual,
    cleaning_performance: n.cleaning_performance,
    duration_overall: n.duration_overall ?? n.duration_actual,
    duration_planned: n.duration_planned ?? 0,
    travel_distance_planned: 0,
    travel_distance_actual: 0,
    water_level_begin: 0,
    water_level_end: 0,
    dirty_water_level_begin: 0,
    dirty_water_level_end: 0,
    garbage_filled_begin: 0,
    garbage_filled_end: 0,
    battery_begin: n.battery_begin,
    battery_end: n.battery_end,
  }
  const origin = n.vendor_origin_record_id
  return {
    id: 0,
    event_id: n.event_id,
    odyssey_id: typeof origin === 'number' ? origin : (0 as any),
    robot_sn: n.robot_sn as any,
    status: 0,
    overall_progress: overall,
    missions_info: (n.missions_info ?? []).map(m => ({ id: typeof m.id === 'number' ? m.id : 0, name: m.name ?? '' })),
    missions_progress: (n.missions_progress ?? [overall]) as unknown as OdysseyEventStatistics['missions_progress'],
    created_at: n.start_time || n.created_at,
    updated_at: n.end_time || n.start_time || n.created_at,
    ...(typeof n.scenario_or_map_id === 'number' && { scenario_id: n.scenario_or_map_id }),
  } as unknown as OdysseyEventStatistics
}

/** useNormalizedApi: two requests to normalized API (source=autonomics, source=gausium_task_reports); UI unblocks when autonomics is back. Default: legacy three endpoints. */
const Dashboard = ({ useNormalizedApi = false }: { useNormalizedApi?: boolean } = {}) => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const initialRobotSn = (searchParams.get('robot_sn') || '').trim()
  const initialTaskId = (searchParams.get('taskId') || '').trim()
  const initialMissionId = (searchParams.get('missionId') || '').trim()
  const [loading, setLoading] = useState(true)
  const fetchIdRef = useRef(0)
  const settledRef = useRef({ event: false, odyssey: false, gausium: false })
  const eventResultsRef = useRef<EventStatistics[]>([])
  const odysseyResultsRef = useRef<OdysseyEventStatistics[]>([])
  const gausiumResultsRef = useRef<any[]>([])

  // Raw Data (separate so UI updates as each source arrives)
  const [eventStatisticsResults, setEventStatisticsResults] = useState<EventStatistics[]>([])
  const [gausiumTaskReportResults, setGausiumTaskReportResults] = useState<EventStatistics[]>([])
  const [rawOdysseys, setRawOdysseys] = useState<OdysseyEventStatistics[]>([])
  const [gausiumRobotSns, setGausiumRobotSns] = useState<string[]>([])

  // Merged events for filters/charts
  const rawEvents = useMemo(
    () => [...eventStatisticsResults, ...gausiumTaskReportResults],
    [eventStatisticsResults, gausiumTaskReportResults]
  )

  // Filters
  const [filterRobotSn, setFilterRobotSn] = useState<string>(initialRobotSn)
  const [filterMissionId, setFilterMissionId] = useState<string>(initialMissionId)
  const [filterTaskId, setFilterTaskId] = useState<string>(initialTaskId)
  const [filterFacilityId, setFilterFacilityId] = useState<string>('')
  const [filterVendor, setFilterVendor] = useState<VendorCode | ''>('')
  const [facilities, setFacilities] = useState<any[]>([])
  const [robotNamesBySn, setRobotNamesBySn] = useState<Record<string, string>>({})
  const [robotVendorsBySn, setRobotVendorsBySn] = useState<Record<string, VendorCode>>({})
  const [robotSnById, setRobotSnById] = useState<Record<number, string>>({})
  const [facilityRobotSns, setFacilityRobotSns] = useState<Set<string>>(new Set())
  const [facilityRobotSnsById, setFacilityRobotSnsById] = useState<Record<string, Set<string>>>({})
  const [facilityRobotSnsByIdReady, setFacilityRobotSnsByIdReady] = useState<boolean>(false)
  const [autoExtendDays, setAutoExtendDays] = useState<number>(0)

  // Date Filters
  const [dateRangeType, setDateRangeType] = useState<'day' | 'week' | 'month' | 'custom'>('month')
  const [customStartDate, setCustomStartDate] = useState<string>(format(subDays(new Date(), 30), 'yyyy-MM-dd'))
  const [customEndDate, setCustomEndDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'))

  // 1a. Calculate Effective Date Range
  const effectiveDateRange = useMemo(() => {
    let startDate: Date
    let endDate: Date = new Date()

    switch (dateRangeType) {
      case 'day':
        startDate = startOfDay(new Date())
        endDate = endOfDay(new Date())
        break
      case 'week':
        startDate = startOfDay(subWeeks(new Date(), 1))
        break
      case 'month':
        startDate = startOfDay(subDays(new Date(), 30 + autoExtendDays))
        break
      case 'custom':
        // Handle potential invalid dates gracefully
        const parsedStart = parseISO(customStartDate)
        const parsedEnd = parseISO(customEndDate)
        if (!isNaN(parsedStart.getTime()) && !isNaN(parsedEnd.getTime())) {
          startDate = startOfDay(parsedStart)
          endDate = endOfDay(parsedEnd)
        } else {
          // Fallback
          startDate = startOfDay(subDays(new Date(), 30))
        }
        break
      default:
        startDate = startOfDay(subDays(new Date(), 30))
    }
    return { startDate, endDate }
  }, [dateRangeType, customStartDate, customEndDate])

  // 1b. Fetch Data
  // useNormalizedApi: two requests to normalized API (autonomics = event+odyssey from DB, gausium = GS); UI unblocks when autonomics is back.
  // else: legacy three endpoints in parallel.
  useEffect(() => {
    const { startDate, endDate } = effectiveDateRange
    const fetchId = Date.now()
    fetchIdRef.current = fetchId
    settledRef.current = { event: false, odyssey: false, gausium: false }
    eventResultsRef.current = []
    odysseyResultsRef.current = []
    gausiumResultsRef.current = []
    setLoading(true)
    setEventStatisticsResults([])
    setGausiumTaskReportResults([])
    setRawOdysseys([])
    setGausiumRobotSns([])

    if (useNormalizedApi) {
      const params: { start_date: string; end_date: string; robot_sn?: string } = {
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
      }
      // Do not pass robot_sn to the backend in normalized mode.
      // Otherwise the backend returns only one robot's rows, which makes the robot dropdown
      // impossible to switch without first resetting to "All Robots".

      const maybeDone = () => {
        const s = settledRef.current
        if (!s.event || !s.odyssey) return
        setLoading(false)
        const eventResults = eventResultsRef.current
        const odysseyResults = odysseyResultsRef.current
        const gausiumResults = gausiumResultsRef.current
        const hasDataForFocusedRobot =
          filterRobotSn &&
          [...eventResults, ...odysseyResults, ...gausiumResults].some(
            (e: any) => String(e.robot_sn).trim() === filterRobotSn.trim()
          )
        if (filterRobotSn && !hasDataForFocusedRobot && dateRangeType === 'month' && autoExtendDays < 150) {
          setAutoExtendDays((prev: number) => prev + 30)
        }
      }

      const logTiming = (label: string, start: number, err?: boolean) => {
        const ms = Math.round(performance.now() - start)
        console.log(`[Dashboard normalized] ${label} ${err ? 'failed' : 'done'} in ${ms} ms`)
      }
      console.log('[Dashboard normalized] fetch started', {
        start: startDate.toISOString(),
        end: endDate.toISOString(),
      })
      const tAutonomics = performance.now()
      const tGausium = performance.now()

      MainApi.fetchDashboardNormalized({ ...params, source: 'autonomics' })
        .then(res => {
          logTiming('autonomics', tAutonomics)
          if (res?.timings_ms) console.log('[Dashboard normalized] timings_ms', res.timings_ms)
          if (fetchIdRef.current !== fetchId) return
          const list = (res?.results ?? []) as NormalizedItem[]
          const events: (EventStatistics & { vendor?: VendorCode; map?: string })[] = []
          const odysseys: OdysseyEventStatistics[] = []
          list.forEach(item => {
            if (item.vendor_origin_record_type === 'event_statistics') {
              events.push(normalizedToEventLike(item))
            } else if (item.vendor_origin_record_type === 'odyssey_event_statistics') {
              odysseys.push(normalizedToOdysseyLike(item))
            }
          })
          eventResultsRef.current = events
          odysseyResultsRef.current = odysseys
          setEventStatisticsResults(events)
          setRawOdysseys(odysseys)
          settledRef.current = { ...settledRef.current, event: true, odyssey: true }
          maybeDone()
        })
        .catch(() => {
          logTiming('autonomics', tAutonomics, true)
          if (fetchIdRef.current !== fetchId) return
          settledRef.current = { ...settledRef.current, event: true, odyssey: true }
          maybeDone()
        })

      // Phase 1: instant cached data (no GS API calls).
      MainApi.fetchDashboardNormalized({ ...params, source: 'gausium_task_reports', cache_only: true })
        .then(res => {
          logTiming('gausium-task-reports-cache', tGausium)
          if (res?.timings_ms) console.log('[Dashboard normalized] timings_ms', res.timings_ms)
          if (fetchIdRef.current !== fetchId) return
          const list = (res?.results ?? []) as NormalizedItem[]
          const gausium: (EventStatistics & { vendor?: VendorCode; map?: string })[] = []
          const gausiumSns = new Set<string>()
          list.forEach(item => {
            gausium.push(normalizedToEventLike(item))
            gausiumSns.add(String(item.robot_sn).trim())
          })
          gausiumResultsRef.current = gausium
          setGausiumTaskReportResults(gausium)
          setGausiumRobotSns(Array.from(gausiumSns))
          settledRef.current = { ...settledRef.current, gausium: true }
          maybeDone()

          // Phase 2: revalidate from GS API for missing days, then refresh UI.
          const tGausiumRefresh = performance.now()
          MainApi.fetchDashboardNormalized({ ...params, source: 'gausium_task_reports' })
            .then(res2 => {
              logTiming('gausium-task-reports-refresh', tGausiumRefresh)
              if (res2?.timings_ms) console.log('[Dashboard normalized] timings_ms', res2.timings_ms)
              if (fetchIdRef.current !== fetchId) return
              const list2 = (res2?.results ?? []) as NormalizedItem[]
              const gausium2: (EventStatistics & { vendor?: VendorCode; map?: string })[] = []
              const gausiumSns2 = new Set<string>()
              list2.forEach(item => {
                gausium2.push(normalizedToEventLike(item))
                gausiumSns2.add(String(item.robot_sn).trim())
              })
              gausiumResultsRef.current = gausium2
              setGausiumTaskReportResults(gausium2)
              setGausiumRobotSns(Array.from(gausiumSns2))
            })
            .catch(() => {
              logTiming('gausium-task-reports-refresh', tGausiumRefresh, true)
            })
        })
        .catch(() => {
          logTiming('gausium-task-reports-cache', tGausium, true)
          if (fetchIdRef.current !== fetchId) return
          settledRef.current = { ...settledRef.current, gausium: true }
          maybeDone()
        })
      return
    }

    const logPrefix = '[Dashboard]'
    const pageParams = {
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
      page_size: 1000,
      page: 1,
    }

    const fetchAllPages = async (apiCall: (params: any) => Promise<{ results: any[]; total: number }>) => {
      const firstPage = await apiCall({ ...pageParams, page: 1 })
      const firstResults = firstPage?.results ?? []
      let allResults = [...firstResults]
      const total = firstPage?.total ?? 0
      if (total > 1000) {
        const totalPages = Math.ceil(total / 1000)
        const restPages = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) => apiCall({ ...pageParams, page: i + 2 }))
        )
        restPages.forEach(res => {
          allResults = [...allResults, ...(res.results ?? [])]
        })
      }
      return { results: allResults, total }
    }

    const gausiumParams: { start_date: string; end_date: string; robot_sn?: string } = {
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
    }
    if (filterRobotSn?.trim()) {
      gausiumParams.robot_sn = filterRobotSn.trim()
    }

    const maybeDone = () => {
      const s = settledRef.current
      // Снимаем общий loading, когда готовы данные из БД (event + odyssey).
      // Gausium может догружаться дольше, но не блокирует отображение дашборда.
      if (!s.event || !s.odyssey) return
      setLoading(false)
      // Auto-extend when focused robot has no data in the selected range
      const eventResults = eventResultsRef.current
      const odysseyResults = odysseyResultsRef.current
      const gausiumResults = gausiumResultsRef.current
      const hasDataForFocusedRobot =
        filterRobotSn &&
        [...eventResults, ...odysseyResults, ...gausiumResults].some(
          (e: any) => String(e.robot_sn).trim() === filterRobotSn.trim()
        )
      if (filterRobotSn && !hasDataForFocusedRobot && dateRangeType === 'month' && autoExtendDays < 150) {
        setAutoExtendDays((prev: number) => prev + 30)
      }
    }

    const onFulfill = (source: 'event' | 'odyssey' | 'gausium') => {
      return (data: any) => {
        if (fetchIdRef.current !== fetchId) return
        if (source === 'event') {
          const results = data?.results ?? []
          eventResultsRef.current = results
          setEventStatisticsResults(results)
        } else if (source === 'odyssey') {
          const results = data?.results ?? []
          odysseyResultsRef.current = results
          setRawOdysseys(results)
        } else {
          const results = data?.results ?? []
          const sns = data?.gausium_robot_sns ?? []
          gausiumResultsRef.current = results
          setGausiumTaskReportResults(results)
          setGausiumRobotSns(sns)
        }
        settledRef.current = { ...settledRef.current, [source]: true }
        maybeDone()
      }
    }

    const onReject = (source: 'event' | 'odyssey' | 'gausium') => {
      if (fetchIdRef.current !== fetchId) return
      settledRef.current = { ...settledRef.current, [source]: true }
      maybeDone()
    }

    const logTiming = (source: string, start: number, err?: boolean) => {
      const ms = Math.round(performance.now() - start)
      console.log(`${logPrefix} ${source} ${err ? 'failed' : 'done'} in ${ms} ms`)
    }

    console.log(`${logPrefix} fetch started`, { start: startDate.toISOString(), end: endDate.toISOString() })
    const tEvent = performance.now()
    const tOdyssey = performance.now()
    const tGausium = performance.now()

    fetchAllPages(MainApi.fetchEventStatistics)
      .then(data => {
        logTiming('event-statistics', tEvent)
        onFulfill('event')(data)
      })
      .catch(() => {
        logTiming('event-statistics', tEvent, true)
        onReject('event')
      })

    fetchAllPages(MainApi.fetchOdysseyEventStatistics)
      .then(data => {
        logTiming('odyssey-statistics', tOdyssey)
        onFulfill('odyssey')(data)
      })
      .catch(() => {
        logTiming('odyssey-statistics', tOdyssey, true)
        onReject('odyssey')
      })

    MainApi.fetchGausiumTaskReports(gausiumParams)
      .then(data => {
        logTiming('gausium-task-reports', tGausium)
        onFulfill('gausium')(data)
      })
      .catch(() => {
        logTiming('gausium-task-reports', tGausium, true)
        onReject('gausium')
      })
  }, [effectiveDateRange, filterRobotSn, filterMissionId, filterTaskId, useNormalizedApi])

  // 1c. Fetch Facilities & Selected Facility Robots
  useEffect(() => {
    MainApi.fetchFacilities(1, 100).then(res => setFacilities(res.results))
  }, [])

  useEffect(() => {
    RobotApi.fetchRobots(1, 1000)
      .then((res: { results: Array<{ id?: number; serialNumber?: string; name?: string; vendor?: VendorCode }> }) => {
        const mapping: Record<string, string> = {}
        const vendorMapping: Record<string, VendorCode> = {}
        const idMapping: Record<number, string> = {}
        ;(res?.results ?? []).forEach(robot => {
          const id = typeof robot?.id === 'number' ? robot.id : null
          const sn = String(robot?.serialNumber ?? '').trim()
          const name = String(robot?.name ?? '').trim()
          if (sn && name) mapping[sn] = name
          if (sn && robot?.vendor) vendorMapping[sn] = robot.vendor
          if (id != null && sn) idMapping[id] = sn
        })
        setRobotNamesBySn(mapping)
        setRobotVendorsBySn(vendorMapping)
        setRobotSnById(idMapping)
      })
      .catch(() => {
        setRobotNamesBySn({})
        setRobotVendorsBySn({})
        setRobotSnById({})
      })
  }, [])

  // When opening dashboard for a specific GS robot (from assets/robots list),
  // do not force default vendor=Autonomics, otherwise GS stats are filtered out.
  useEffect(() => {
    const sn = filterRobotSn?.trim()
    if (!sn) return
    const vendor = robotVendorsBySn[sn]
    if (vendor === VENDOR.GAUSIUM && filterVendor === VENDOR.AUTONOMICS) {
      setFilterVendor('')
    }
  }, [filterRobotSn, robotVendorsBySn, filterVendor])

  // Build facility -> robot SNs mapping so we can filter facilities dynamically.
  useEffect(() => {
    if (!facilities?.length) {
      setFacilityRobotSnsById({})
      setFacilityRobotSnsByIdReady(false)
      return
    }
    // If we don't have robots mapping yet, wait.
    if (!robotSnById || Object.keys(robotSnById).length === 0) return

    let cancelled = false
    const load = async () => {
      try {
        setFacilityRobotSnsByIdReady(false)
        const pairs = await Promise.all(
          facilities.map(async (f: any) => {
            const facId = String(f.id)
            try {
              const full = await MainApi.fetchFacility(Number(f.id))
              const ids: number[] = full?.robots_ids || []
              const sns = new Set<string>()
              ids.forEach(id => {
                const sn = robotSnById[id]
                if (sn) sns.add(String(sn).trim())
              })
              return [facId, sns] as const
            } catch {
              return [facId, new Set<string>()] as const
            }
          })
        )
        if (cancelled) return
        const next: Record<string, Set<string>> = {}
        pairs.forEach((pair: readonly [string, Set<string>]) => {
          const id = pair[0]
          const sns = pair[1]
          next[id] = sns
        })
        setFacilityRobotSnsById(next)
        setFacilityRobotSnsByIdReady(true)
      } catch {
        if (!cancelled) {
          setFacilityRobotSnsById({})
          setFacilityRobotSnsByIdReady(true)
        }
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [facilities, robotSnById])

  useEffect(() => {
    if (filterFacilityId) {
      if (filterFacilityId === FACILITY_NONE_ID) {
        // If mapping isn't ready yet, avoid showing misleading results.
        if (!facilityRobotSnsByIdReady) {
          setFacilityRobotSns(new Set())
          return
        }
        const allInAnyFacility = new Set<string>()
        ;(Object.values(facilityRobotSnsById) as Set<string>[]).forEach((sns: Set<string>) => {
          sns.forEach((sn: string) => allInAnyFacility.add(sn))
        })
        const allKnownRobotSns = new Set<string>()
        ;[...rawEvents, ...rawOdysseys].forEach((item: any) => {
          const sn = String(item?.robot_sn ?? '').trim()
          if (sn) allKnownRobotSns.add(sn)
        })
        const withoutFacility = new Set<string>()
        allKnownRobotSns.forEach(sn => {
          if (!allInAnyFacility.has(sn)) withoutFacility.add(sn)
        })
        setFacilityRobotSns(withoutFacility)
        return
      }

      // Use preloaded facility -> SN mapping (no extra requests).
      if (!facilityRobotSnsByIdReady) {
        setFacilityRobotSns(new Set())
        return
      }
      const sns = facilityRobotSnsById[String(filterFacilityId)] || new Set<string>()
      setFacilityRobotSns(new Set(Array.from(sns)))
    } else {
      setFacilityRobotSns(new Set())
    }
  }, [filterFacilityId, facilityRobotSnsById, facilityRobotSnsByIdReady, rawEvents, rawOdysseys])
  // 2. Filter Logic (Client-Side)
  const filteredData = useMemo(() => {
    const filterSn = filterRobotSn ? String(filterRobotSn).trim() : null
    const getVendor = (item: any): VendorCode => (item.vendor === VENDOR.GAUSIUM ? VENDOR.GAUSIUM : VENDOR.AUTONOMICS)
    const filterMissionNum = filterMissionId && !filterMissionId.startsWith('map:') ? Number(filterMissionId) : null
    const hasFilterMissionNum = filterMissionNum != null && Number.isFinite(filterMissionNum)

    const events = rawEvents.filter((e: any) => {
      if (filterVendor && getVendor(e) !== filterVendor) return false
      // Facility
      if (filterFacilityId && !facilityRobotSns.has(String(e.robot_sn).trim())) return false
      // Robot SN
      if (filterSn && String(e.robot_sn).trim() !== filterSn) return false
      // Mission: Autonomics = mission_id, Gausium = map (id "map:...")
      if (filterMissionId) {
        if (filterMissionId.startsWith('map:')) {
          if (e.vendor !== VENDOR.GAUSIUM || e.map !== filterMissionId.slice(4)) return false
        } else if (
          !(
            (hasFilterMissionNum && e.mission_id === filterMissionNum) ||
            (hasFilterMissionNum && e.scenario_id === filterMissionNum)
          )
        )
          return false
      }
      // Task: Autonomics = task_id, Gausium = mission_progress.name (id "task:...")
      if (filterTaskId) {
        if (filterTaskId.startsWith('task:')) {
          if (e.mission_progress?.name !== filterTaskId.slice(5)) return false
        } else if (e.task_id !== Number(filterTaskId)) return false
      }
      return true
    })

    const odysseys = rawOdysseys.filter((o: any) => {
      if (filterVendor && getVendor(o) !== filterVendor) return false
      // Facility
      if (filterFacilityId && !facilityRobotSns.has(String(o.robot_sn).trim())) return false
      // Robot SN
      if (filterSn && String(o.robot_sn).trim() !== filterSn) return false
      // Map (Gausium) filter doesn't apply to Odysseys: without this, selecting map:Asbis still includes Odyssey stats
      // (e.g. robots 1000/12345), inflating "tasks" count for the selected map.
      if (filterMissionId && filterMissionId.startsWith('map:')) return false
      if (
        filterMissionId &&
        !(
          (hasFilterMissionNum && o.scenario_id === filterMissionNum) ||
          (o.missions_info || []).some((m: any) => m.id === filterMissionNum)
        )
      )
        return false
      if (filterTaskId && !filterTaskId.startsWith('task:') && o.odyssey_id !== Number(filterTaskId)) return false
      return true
    })

    return { events, odysseys }
  }, [
    rawEvents,
    rawOdysseys,
    filterRobotSn,
    filterMissionId,
    filterTaskId,
    filterFacilityId,
    filterVendor,
    facilityRobotSns,
  ])

  // 3. Aggregated Stats
  const stats = useMemo(() => {
    let totalArea = 0
    let totalTime = 0
    let totalOverallTime = 0

    // Process Events
    filteredData.events.forEach(e => {
      const area = e.mission_progress?.cleaned_area_actual || 0
      const time = e.mission_progress?.duration_actual || 0
      const overallTime = e.mission_progress?.duration_overall || e.mission_progress?.duration_actual || 0
      totalArea += area
      totalTime += time
      totalOverallTime += overallTime
    })

    // Process Odysseys
    filteredData.odysseys.forEach(o => {
      const area = o.overall_progress?.cleaned_area_actual || 0
      const time = o.overall_progress?.duration_actual || 0
      const overallTime = o.overall_progress?.duration_overall || o.overall_progress?.duration_actual || 0
      totalArea += area
      totalTime += time
      totalOverallTime += overallTime
    })

    // Weighted average: total area / total time in hours
    const totalTimeHours = totalTime / 3600
    const avgPerformance = totalTimeHours > 0 ? totalArea / totalTimeHours : 0

    return {
      totalArea,
      totalTime,
      totalOverallTime,
      avgPerformance,
    }
  }, [filteredData])

  // 4. Chart Data
  const chartData = useMemo(() => {
    const aggregatedData: Record<string, { area: number; time: number; overallTime: number; eventsCount: number }> = {}

    filteredData.events.forEach(e => {
      const date = format(parseISO(e.created_at), 'yyyy-MM-dd')
      const area = e.mission_progress?.cleaned_area_actual || 0
      const time = e.mission_progress?.duration_actual || 0
      const overallTime = e.mission_progress?.duration_overall || e.mission_progress?.duration_actual || 0

      if (!aggregatedData[date]) aggregatedData[date] = { area: 0, time: 0, overallTime: 0, eventsCount: 0 }
      aggregatedData[date].area += area
      aggregatedData[date].time += time
      aggregatedData[date].overallTime += overallTime
      aggregatedData[date].eventsCount += 1
    })

    filteredData.odysseys.forEach(o => {
      const date = format(parseISO(o.created_at), 'yyyy-MM-dd')
      const area = o.overall_progress?.cleaned_area_actual || 0
      const time = o.overall_progress?.duration_actual || 0
      const overallTime = o.overall_progress?.duration_overall || o.overall_progress?.duration_actual || 0

      if (!aggregatedData[date]) aggregatedData[date] = { area: 0, time: 0, overallTime: 0, eventsCount: 0 }
      aggregatedData[date].area += area
      aggregatedData[date].time += time
      aggregatedData[date].overallTime += overallTime
      aggregatedData[date].eventsCount += 1
    })

    // Generate all dates in range
    let allDates: Date[] = []
    try {
      allDates = eachDayOfInterval({
        start: effectiveDateRange.startDate,
        end: effectiveDateRange.endDate,
      })
    } catch (e) {
      const today = new Date()
      allDates = [today]
    }

    return allDates.map(d => {
      const dateKey = format(d, 'yyyy-MM-dd')
      const data = aggregatedData[dateKey] || { area: 0, time: 0, overallTime: 0, eventsCount: 0 }
      return {
        fullDate: dateKey,
        date: format(d, 'MMM dd'),
        area: Math.round(data.area * 100) / 100,
        time: Math.round((data.time / 3600) * 100) / 100,
        overallTime: Math.round((data.overallTime / 3600) * 100) / 100,
        eventsCount: data.eventsCount,
      }
    })
  }, [filteredData, effectiveDateRange])

  // 5. Derived Filter Options (Dynamic)
  const filterOptions = useMemo(() => {
    const allStats = [...rawEvents, ...rawOdysseys]
    const getVendor = (item: any): VendorCode => (item.vendor === VENDOR.GAUSIUM ? VENDOR.GAUSIUM : VENDOR.AUTONOMICS)

    // Maps to check reachability (missions/tasks: Autonomics = numeric id, Gausium = "map:xxx" / "task:xxx")
    const reachableRobotSns = new Set<string>()
    if (filterRobotSn?.trim()) reachableRobotSns.add(filterRobotSn.trim())
    const reachableMissions = new Map<
      number | string,
      { id: number | string; name: string; kind: 'scenario' | 'mission' | 'map' }
    >()
    const reachableTaskIds = new Set<number | string>()
    const taskIdToName = new Map<number | string, string>()

    allStats.forEach((item: any) => {
      const missionIds: (number | string)[] = []
      if (item.scenario_id) {
        missionIds.push(item.scenario_id)
        if (!reachableMissions.has(item.scenario_id)) {
          reachableMissions.set(item.scenario_id, {
            id: item.scenario_id,
            name: `Scenario ${item.scenario_id}`,
            kind: 'scenario',
          })
        }
      }
      if (item.mission_id) missionIds.push(item.mission_id)
      if (item.map) {
        const mapId = `map:${item.map}`
        missionIds.push(mapId)
        reachableMissions.set(mapId, { id: mapId, name: item.map, kind: 'map' })
      }

      if (item.missions_info) {
        item.missions_info.forEach((m: any) => {
          missionIds.push(m.id)
          reachableMissions.set(m.id, { id: m.id, name: m.name, kind: 'mission' })
        })
      }

      if (item.mission_id && !reachableMissions.has(item.mission_id)) {
        reachableMissions.set(item.mission_id, {
          id: item.mission_id,
          name: `Mission ${item.mission_id}`,
          kind: 'mission',
        })
      }

      const effectiveTaskId: number | string =
        item.vendor === VENDOR.GAUSIUM
          ? item.mission_progress?.name && item.map
            ? `task:${item.mission_progress.name}`
            : null
          : item.task_id ?? item.odyssey_id ?? null
      if (effectiveTaskId != null) {
        const taskName =
          typeof effectiveTaskId === 'string' && effectiveTaskId.startsWith('task:')
            ? effectiveTaskId.slice(5)
            : `Task ${effectiveTaskId}`
        taskIdToName.set(effectiveTaskId, taskName)
      }

      const matchesVendor = !filterVendor || getVendor(item) === filterVendor
      const matchesFacility = !filterFacilityId || facilityRobotSns.has(String(item.robot_sn).trim())
      const matchesRobot = !filterRobotSn || String(item.robot_sn).trim() === String(filterRobotSn).trim()
      const matchesMission =
        !filterMissionId || missionIds.includes(filterMissionId) || missionIds.includes(Number(filterMissionId))
      const matchesTask =
        !filterTaskId ||
        effectiveTaskId === filterTaskId ||
        (filterTaskId && typeof effectiveTaskId === 'number' && effectiveTaskId === Number(filterTaskId))

      if (matchesVendor && matchesFacility && matchesRobot && matchesMission && effectiveTaskId != null) {
        reachableTaskIds.add(effectiveTaskId)
      }
      if (matchesVendor && matchesFacility && matchesMission && (!filterTaskId || matchesTask)) {
        reachableRobotSns.add(String(item.robot_sn).trim())
      }
    })

    const finalReachableMissionIds = new Set<number | string>()
    allStats.forEach((item: any) => {
      const effectiveTaskId =
        item.vendor === VENDOR.GAUSIUM
          ? item.mission_progress?.name && item.map
            ? `task:${item.mission_progress.name}`
            : null
          : item.task_id ?? item.odyssey_id ?? null
      const matchesFacility = !filterFacilityId || facilityRobotSns.has(String(item.robot_sn).trim())
      const matchesRobot = !filterRobotSn || String(item.robot_sn).trim() === String(filterRobotSn).trim()
      const matchesTask =
        !filterTaskId ||
        effectiveTaskId === filterTaskId ||
        (filterTaskId && typeof effectiveTaskId === 'number' && effectiveTaskId === Number(filterTaskId))
      const matchesVendor = !filterVendor || getVendor(item) === filterVendor

      if (matchesVendor && matchesFacility && matchesRobot && matchesTask) {
        if (item.scenario_id) finalReachableMissionIds.add(item.scenario_id)
        if (item.mission_id) finalReachableMissionIds.add(item.mission_id)
        if (item.map) finalReachableMissionIds.add(`map:${item.map}`)
        if (item.missions_info) item.missions_info.forEach((m: any) => finalReachableMissionIds.add(m.id))
      }
    })

    // Only robots that have events matching current mission/task filters (same logic as Autonomics; do not add all GS robots when filter is set)
    const robotsList = Array.from(reachableRobotSns)
      .sort()
      .map(sn => ({
        id: sn,
        serialNumber: sn,
        name: sn,
      }))

    const missionsList = Array.from(finalReachableMissionIds)
      .map(id => {
        const raw = reachableMissions.get(id) || {
          id,
          name: typeof id === 'string' ? String(id).replace(/^map:/, '') : `Mission ${id}`,
          kind: (typeof id === 'string' && String(id).startsWith('map:') ? 'map' : 'mission') as
            | 'scenario'
            | 'mission'
            | 'map',
        }
        if (typeof raw.id === 'number') {
          return { ...raw, displayName: `${raw.id} - ${raw.name}` }
        }
        return { ...raw, displayName: raw.name }
      })
      .sort((a, b) => {
        const order = { scenario: 0, mission: 1, map: 2 }
        const byKind = order[a.kind] - order[b.kind]
        if (byKind !== 0) return byKind
        return String(a.displayName).localeCompare(String(b.displayName))
      })

    const tasksList = Array.from(reachableTaskIds)
      .map(id => ({
        id,
        name:
          typeof id === 'number'
            ? `${id} - ${taskIdToName.get(id) || `Task ${id}`}`
            : taskIdToName.get(id) || String(id).replace(/^task:/, ''),
      }))
      .sort((a, b) => String(a.name).localeCompare(String(b.name)))

    if (filterRobotSn && !robotsList.some(r => String(r.serialNumber).trim() === filterRobotSn.trim())) {
      robotsList.unshift({ id: filterRobotSn, serialNumber: filterRobotSn, name: filterRobotSn })
    }
    if (filterMissionId && !missionsList.some(m => String(m.id) === filterMissionId)) {
      const isMap = filterMissionId.startsWith('map:')
      const id = isMap ? filterMissionId : Number(filterMissionId)
      const name = isMap ? filterMissionId.slice(4) : `Mission ${filterMissionId}`
      const kind = (isMap ? 'map' : 'mission') as 'scenario' | 'mission' | 'map'
      const displayName = typeof id === 'number' ? `${id} - ${name}` : name
      missionsList.unshift({ id, name, kind, displayName })
    }
    if (filterTaskId && !tasksList.some(t => String(t.id) === filterTaskId)) {
      const name = filterTaskId.startsWith('task:') ? filterTaskId.slice(5) : `Task ${filterTaskId}`
      tasksList.unshift({ id: filterTaskId.startsWith('task:') ? filterTaskId : Number(filterTaskId), name })
    }

    return {
      robots: robotsList,
      missions: missionsList,
      tasks: tasksList,
    }
  }, [
    rawEvents,
    rawOdysseys,
    gausiumRobotSns,
    filterRobotSn,
    filterMissionId,
    filterTaskId,
    filterFacilityId,
    filterVendor,
    facilityRobotSns,
  ])

  const reachableVendors = useMemo(() => {
    const allStats = [...rawEvents, ...rawOdysseys]
    const getVendor = (item: any): VendorCode => (item.vendor === VENDOR.GAUSIUM ? VENDOR.GAUSIUM : VENDOR.AUTONOMICS)
    const vendors = new Set<VendorCode>()

    allStats.forEach((item: any) => {
      const sn = String(item.robot_sn ?? '').trim()
      if (!sn) return

      // Respect already applied filters except vendor itself.
      if (filterFacilityId && !facilityRobotSns.has(sn)) return
      if (filterRobotSn && String(filterRobotSn).trim() && sn !== String(filterRobotSn).trim()) return

      // Mission filter (scenario/mission/map)
      if (filterMissionId) {
        if (filterMissionId.startsWith('map:')) {
          const mapId = filterMissionId.slice(4)
          if (item.vendor !== VENDOR.GAUSIUM || item.map !== mapId) return
        } else {
          const num = Number(filterMissionId)
          if (!Number.isFinite(num)) return
          const inMissionsInfo = (item.missions_info || []).some((m: any) => m.id === num)
          if (!(item.mission_id === num || item.scenario_id === num || inMissionsInfo)) return
        }
      }

      // Task filter
      if (filterTaskId) {
        if (filterTaskId.startsWith('task:')) {
          const name = filterTaskId.slice(5)
          if (item.vendor !== VENDOR.GAUSIUM || item.mission_progress?.name !== name) return
        } else {
          const num = Number(filterTaskId)
          if (!Number.isFinite(num)) return
          if (!(item.task_id === num || item.odyssey_id === num)) return
        }
      }

      vendors.add(getVendor(item))
    })

    return Array.from(vendors)
  }, [rawEvents, rawOdysseys, filterFacilityId, facilityRobotSns, filterRobotSn, filterMissionId, filterTaskId])

  const filteredFacilities = useMemo(() => {
    if (!facilities?.length) return []

    // If we don't have mapping yet, do not over-filter.
    if (!facilityRobotSnsById || Object.keys(facilityRobotSnsById).length === 0) return facilities

    // If no other filters applied, keep all.
    const hasOtherFilters =
      Boolean(filterRobotSn?.trim()) || Boolean(filterMissionId) || Boolean(filterTaskId) || Boolean(filterVendor)

    const allStats = [...rawEvents, ...rawOdysseys]
    const getVendor = (item: any): VendorCode => (item.vendor === VENDOR.GAUSIUM ? VENDOR.GAUSIUM : VENDOR.AUTONOMICS)

    const reachableRobotSns = new Set<string>()
    allStats.forEach((item: any) => {
      const sn = String(item.robot_sn ?? '').trim()
      if (!sn) return
      if (filterVendor && getVendor(item) !== filterVendor) return
      if (filterRobotSn?.trim() && sn !== filterRobotSn.trim()) return

      // Mission filter
      if (filterMissionId) {
        if (filterMissionId.startsWith('map:')) {
          const mapId = filterMissionId.slice(4)
          if (item.vendor !== VENDOR.GAUSIUM || item.map !== mapId) return
        } else {
          const num = Number(filterMissionId)
          if (!Number.isFinite(num)) return
          const inMissionsInfo = (item.missions_info || []).some((m: any) => m.id === num)
          if (!(item.mission_id === num || item.scenario_id === num || inMissionsInfo)) return
        }
      }

      // Task filter
      if (filterTaskId) {
        if (filterTaskId.startsWith('task:')) {
          const name = filterTaskId.slice(5)
          if (item.vendor !== VENDOR.GAUSIUM || item.mission_progress?.name !== name) return
        } else {
          const num = Number(filterTaskId)
          if (!Number.isFinite(num)) return
          if (!(item.task_id === num || item.odyssey_id === num)) return
        }
      }

      reachableRobotSns.add(sn)
    })

    // Add synthetic "no facility" option when some reachable robots are not in any facility.
    let hasReachableWithoutFacility = false
    if (facilityRobotSnsByIdReady) {
      const allInAnyFacility = new Set<string>()
      ;(Object.values(facilityRobotSnsById) as Set<string>[]).forEach((sns: Set<string>) => {
        sns.forEach((sn: string) => allInAnyFacility.add(sn))
      })
      hasReachableWithoutFacility = Array.from(reachableRobotSns).some(sn => !allInAnyFacility.has(sn))
    }

    const next = hasOtherFilters
      ? facilities.filter((f: any) => {
          const sns = facilityRobotSnsById[String(f.id)] || new Set<string>()
          return Array.from(reachableRobotSns).some(sn => sns.has(sn))
        })
      : facilities.slice()

    if (hasReachableWithoutFacility || filterFacilityId === FACILITY_NONE_ID) {
      next.unshift({ id: FACILITY_NONE_ID, name: FACILITY_NONE_LABEL } as any)
    }

    // Keep current selection visible even if mapping is incomplete.
    if (filterFacilityId && !next.some((f: any) => String(f.id) === String(filterFacilityId))) {
      const current = facilities.find((f: any) => String(f.id) === String(filterFacilityId))
      if (current) next.unshift(current)
    }
    return next
  }, [
    facilities,
    facilityRobotSnsById,
    facilityRobotSnsByIdReady,
    rawEvents,
    rawOdysseys,
    filterRobotSn,
    filterMissionId,
    filterTaskId,
    filterVendor,
    filterFacilityId,
  ])

  // Reset stale filters when options no longer include the selected value
  useEffect(() => {
    if (loading) return
    const currentSn = filterRobotSn?.trim()
    if (currentSn && !filterOptions.robots.some(r => String(r.serialNumber).trim() === currentSn)) {
      setFilterRobotSn('')
    }
    if (filterMissionId && !filterOptions.missions.some(m => String(m.id) === filterMissionId)) {
      setFilterMissionId('')
    }
    if (filterTaskId && !filterOptions.tasks.some(t => String(t.id) === filterTaskId)) {
      setFilterTaskId('')
    }
    if (filterFacilityId && !filteredFacilities.some((f: any) => String(f.id) === String(filterFacilityId))) {
      setFilterFacilityId('')
    }
    if (filterVendor && !reachableVendors.includes(filterVendor as VendorCode)) {
      setFilterVendor('')
    }
  }, [
    filterOptions,
    filterRobotSn,
    filterMissionId,
    filterTaskId,
    filteredFacilities,
    reachableVendors,
    filterFacilityId,
    filterVendor,
    loading,
  ])

  const handleClearFilters = () => {
    setFilterRobotSn('')
    setFilterMissionId('')
    setFilterTaskId('')
    setFilterFacilityId('')
    setFilterVendor('')
    setAutoExtendDays(0)
  }

  // Reset auto-extend when filters change manually
  useEffect(() => {
    setAutoExtendDays(0)
  }, [
    filterRobotSn,
    filterMissionId,
    filterTaskId,
    filterFacilityId,
    filterVendor,
    dateRangeType,
    customStartDate,
    customEndDate,
  ])

  const formatTime = (seconds: number) => {
    if (!seconds) return '0h 0m'
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    return `${h}h ${m}m`
  }

  const formatHoursToTime = (hoursTotal: number) => {
    const h = Math.floor(hoursTotal)
    const m = Math.floor((hoursTotal - h) * 60)
    return `${h}h ${m}m`
  }

  const handleExport = () => {
    const combinedStats = [...filteredData.events, ...filteredData.odysseys]
    const events: ProcessedEvent[] = []
    addEventsStatistics(events, combinedStats, [], new Map(), new Map())
    exportScheduleToCSV(events)
  }

  const scenarioMissionOptions = useMemo(
    () => filterOptions.missions.filter((m: any) => m.kind === 'scenario'),
    [filterOptions.missions]
  )
  const missionOptions = useMemo(
    () => filterOptions.missions.filter((m: any) => m.kind === 'mission'),
    [filterOptions.missions]
  )
  const mapOptions = useMemo(
    () => filterOptions.missions.filter((m: any) => m.kind === 'map'),
    [filterOptions.missions]
  )

  return (
    <div className={style.container}>
      <div className={style.header}>
        <Title text={t('header.dashboard')} />
        <Button.Outlined onClick={handleExport} style={{ width: '150px' }}>
          Export Statistics
        </Button.Outlined>
      </div>

      <div className={style.summaryCards}>
        <div className={style.summaryCard}>
          <span className={style.summaryLabel}>Total Area</span>
          <span className={style.summaryValue}>{Math.round(stats.totalArea).toLocaleString()} m²</span>
        </div>
        <div className={style.summaryCard}>
          <span className={style.summaryLabel}>Total Working Time</span>
          <span className={style.summaryValue}>{formatTime(stats.totalTime)}</span>
        </div>
        <div className={style.summaryCard}>
          <span className={style.summaryLabel}>Overall Time</span>
          <span className={style.summaryValue}>{formatTime(stats.totalOverallTime)}</span>
        </div>
        <div className={style.summaryCard}>
          <span className={style.summaryLabel}>Avg Productivity</span>
          <span className={style.summaryValue}>{Math.round(stats.avgPerformance)} m²/h</span>
        </div>
      </div>

      <div className={style.filtersBlock}>
        <div className={style.leftFilters}>
          <select
            className={`${style.filterSelect} ${style.filterDate}`}
            value={dateRangeType}
            onChange={e => setDateRangeType(e.target.value as any)}
          >
            <option value="day">Today</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="custom">Custom Range</option>
          </select>

          {dateRangeType === 'custom' && (
            <div className={style.dateInputs}>
              <input
                type="date"
                className={style.filterDateInput}
                value={customStartDate}
                onChange={e => setCustomStartDate(e.target.value)}
              />
              <span style={{ color: '#64748b' }}>-</span>
              <input
                type="date"
                className={style.filterDateInput}
                value={customEndDate}
                onChange={e => setCustomEndDate(e.target.value)}
              />
            </div>
          )}
        </div>

        <div className={style.filters}>
          <select
            className={`${style.filterSelect} ${style.filterFacility}`}
            value={filterFacilityId}
            onChange={e => setFilterFacilityId(e.target.value)}
          >
            <option value="">All facilities</option>
            {filteredFacilities.map(f => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>

          <select
            className={`${style.filterSelect} ${style.filterVendor}`}
            value={filterVendor}
            onChange={e => setFilterVendor(e.target.value as VendorCode | '')}
          >
            <option value="">All vendors</option>
            {reachableVendors.includes(VENDOR.AUTONOMICS) && <option value={VENDOR.AUTONOMICS}>Autonomics</option>}
            {reachableVendors.includes(VENDOR.GAUSIUM) && <option value={VENDOR.GAUSIUM}>Gausium</option>}
          </select>

          <select
            className={`${style.filterSelect} ${style.filterRobot}`}
            value={filterRobotSn}
            onChange={e => setFilterRobotSn(e.target.value)}
          >
            <option value="">All Robots</option>
            {filterOptions.robots.map(r => (
              <option key={r.serialNumber} value={r.serialNumber}>
                {robotNamesBySn[String(r.serialNumber).trim()]
                  ? `${r.serialNumber} - ${robotNamesBySn[String(r.serialNumber).trim()]}`
                  : r.name || r.serialNumber}
              </option>
            ))}
          </select>

          <select
            className={`${style.filterSelect} ${style.filterMission}`}
            value={filterMissionId}
            onChange={e => setFilterMissionId(e.target.value)}
          >
            <option value="">All Missions</option>
            {scenarioMissionOptions.length > 0 && (
              <optgroup label={t('dashboard.filters.scenarios_group')}>
                {scenarioMissionOptions
                  .slice()
                  .sort((a, b) => {
                    const aNum = Number(a.id)
                    const bNum = Number(b.id)
                    const aIsNum = Number.isFinite(aNum)
                    const bIsNum = Number.isFinite(bNum)
                    if (aIsNum && bIsNum) return aNum - bNum
                    return String(a.displayName).localeCompare(String(b.displayName))
                  })
                  .map(m => (
                    <option key={`scenario-${m.id}`} value={m.id}>
                      {m.displayName}
                    </option>
                  ))}
              </optgroup>
            )}
            {missionOptions.length > 0 && (
              <optgroup label={t('dashboard.filters.missions_group')}>
                {missionOptions
                  .slice()
                  .sort((a, b) => {
                    const aNum = Number(a.id)
                    const bNum = Number(b.id)
                    const aIsNum = Number.isFinite(aNum)
                    const bIsNum = Number.isFinite(bNum)
                    if (aIsNum && bIsNum) return aNum - bNum
                    return String(a.displayName).localeCompare(String(b.displayName))
                  })
                  .map(m => (
                    <option key={`mission-${m.id}`} value={m.id}>
                      {m.displayName}
                    </option>
                  ))}
              </optgroup>
            )}
            {mapOptions.length > 0 && (
              <optgroup label={t('dashboard.filters.maps_group')}>
                {mapOptions.map(m => (
                  <option key={`map-${m.id}`} value={m.id}>
                    {m.displayName}
                  </option>
                ))}
              </optgroup>
            )}
          </select>

          <select
            className={`${style.filterSelect} ${style.filterTask}`}
            value={filterTaskId}
            onChange={e => setFilterTaskId(e.target.value)}
          >
            <option value="">All Tasks</option>
            {filterOptions.tasks.map(t => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>

          <Button.Outlined className={style.clearButton} onClick={handleClearFilters}>
            Clear Filters
          </Button.Outlined>
        </div>
      </div>

      <div className={style.statsGrid}>
        <Box className={style.box}>
          <h3 className={style.cardTitle}>Cleaning History</h3>
          <div className={style.chartContainer}>
            {loading ? (
              <div className={style.loader}>Loading statistical data...</div>
            ) : (
              <ResponsiveContainer width="100%" height={400}>
                <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis
                    dataKey="fullDate"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#64748b', fontSize: 12 }}
                    dy={10}
                    tickFormatter={(value: string) => format(parseISO(value), 'MMM dd')}
                    minTickGap={30}
                  />
                  <YAxis
                    yAxisId="left"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#64748b', fontSize: 12 }}
                    tickFormatter={(value: number) => `${value}`}
                    label={{ value: 'Area (m²)', angle: -90, position: 'insideLeft', fill: '#64748b' }}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#64748b', fontSize: 12 }}
                    label={{ value: 'Time (h)', angle: 90, position: 'insideRight', fill: '#64748b' }}
                  />
                  <Tooltip
                    animationDuration={150}
                    cursor={{ fill: '#f1f5f9' }}
                    contentStyle={{
                      backgroundColor: '#fff',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                      fontSize: '12px',
                    }}
                    formatter={(value: any, name: any, props: any) => {
                      if ((name === 'Working Time (h)' || name === 'Overall Time (h)') && typeof value === 'number') {
                        return [formatHoursToTime(value), name]
                      }
                      return [value, name]
                    }}
                    labelFormatter={(label: any, payload: readonly any[]) => {
                      if (payload && payload.length > 0) {
                        const count = payload[0].payload.eventsCount
                        return count ? `${label} (${count} ${count === 1 ? 'task' : 'tasks'})` : label
                      }
                      return label
                    }}
                  />
                  <Legend wrapperStyle={{ paddingTop: '20px' }} />
                  <Bar
                    yAxisId="left"
                    dataKey="area"
                    name="Cleaned Area (m²)"
                    fill="#4e78f6"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={50}
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill="#4e78f6" fillOpacity={0.8 + (index / chartData.length) * 0.2} />
                    ))}
                  </Bar>
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="overallTime"
                    name="Overall Time (h)"
                    stroke="#4e78f6"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="time"
                    name="Working Time (h)"
                    stroke="#ff7300"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>
        </Box>
      </div>
    </div>
  )
}

export default Dashboard
